import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchWithAuth } from "@/lib/api/client";
import { showToast } from "@/lib/toast";

// ============================================================================
// Types
// ============================================================================

export interface Document {
  id: string;
  licitacion_id: string;
  filename: string;
  file_type: string;
  file_size: number;
  status: string;
  uploaded_by: string;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Query Keys
// ============================================================================

export interface DocumentProcessingStatus {
  document_id: string;
  // El backend serializa el campo SQLAlchemy `estado_procesamiento`. Antes
  // este tipo lo llamaba `estado`, lo que rompía el polling: el frontend
  // nunca veía COMPLETADO/ERROR porque el campo era `undefined`, así que el
  // refetchInterval no paraba y los callbacks de éxito/error nunca disparaban.
  estado_procesamiento: string;
  // Float 0.0-1.0 (no porcentaje). El frontend lo multiplica por 100 para
  // mostrarlo en la UI; tratarlo como porcentaje directo causa "0.7%" en
  // lugar de "70%".
  processing_progress: number;
  processing_error: string | null;
  num_chunks: number | null;
  processing_started_at: string | null;
  processing_completed_at: string | null;
}

const documentKeys = {
  all: ["documents"] as const,
  list: (licitacionId: string) => [...documentKeys.all, "list", licitacionId] as const,
  processingStatus: (documentId: string) =>
    [...documentKeys.all, "processing-status", documentId] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook para obtener la lista de documentos de una licitacion
 */
export function useDocuments(licitacionId: string) {
  return useQuery<Document[], Error>({
    queryKey: documentKeys.list(licitacionId),
    queryFn: () => fetchWithAuth(`/api/v1/licitaciones/${licitacionId}/documentos`),
    enabled: !!licitacionId,
  });
}

/**
 * Hook para polling del estado de procesamiento de un documento.
 * Hace polling cada 2s mientras el documento se procesa y se detiene
 * cuando alcanza COMPLETADO o ERROR.
 */
export function useDocumentProcessingStatus(
  documentId: string,
  enabled: boolean = false
) {
  return useQuery<DocumentProcessingStatus>({
    queryKey: documentKeys.processingStatus(documentId),
    queryFn: () =>
      fetchWithAuth(`/api/v1/documentos/${documentId}/processing-status`),
    enabled: enabled && !!documentId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (
        data?.estado_procesamiento === "COMPLETADO" ||
        data?.estado_procesamiento === "ERROR"
      ) {
        return false;
      }
      return 2000;
    },
    staleTime: 0,
  });
}

/**
 * Hook para subir un documento a una licitacion (FormData upload)
 */
export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation<Document, Error, { licitacionId: string; file: File }>({
    mutationFn: async ({ licitacionId, file }) => {
      const formData = new FormData();
      formData.append("file", file);

      return fetchWithAuth(`/api/v1/licitaciones/${licitacionId}/documentos`, {
        method: "POST",
        body: formData,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.list(data.licitacion_id),
      });
      showToast.success("Documento subido correctamente");
    },
    onError: () => {
      showToast.error("Error al subir el documento");
    },
  });
}

/**
 * Hook para eliminar un documento
 */
export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { documentId: string; licitacionId: string }>({
    mutationFn: ({ documentId }) =>
      fetchWithAuth(`/api/v1/documentos/${documentId}`, {
        method: "DELETE",
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.list(variables.licitacionId),
      });
      showToast.success("Documento eliminado");
    },
    onError: () => {
      showToast.error("Error al eliminar el documento");
    },
  });
}

/**
 * Hook para reprocesar un documento (re-extraer texto, re-generar embeddings)
 */
export function useReprocessDocument() {
  const queryClient = useQueryClient();

  return useMutation<Document, Error, { documentId: string; licitacionId: string }>({
    mutationFn: ({ documentId }) =>
      fetchWithAuth(`/api/v1/documentos/${documentId}/reprocess`, {
        method: "POST",
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: documentKeys.list(variables.licitacionId),
      });
      showToast.success("Reprocesamiento iniciado");
    },
    onError: () => {
      showToast.error("Error al reprocesar el documento");
    },
  });
}
